import React, { useMemo } from 'react';
import type { ImageStyle, StyleProp, ViewStyle } from 'react-native';
import { View, TouchableOpacity, Text, Image, StyleSheet, Platform } from 'react-native';
import FadeIn from 'react-native-fade-in-image';
import checkIsEven from 'src/core/utils/checkIsEven';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Images from 'src/core/utils/Images';
import Constants from 'src/core/utils/Constants';
import type { ApiGallery, ApiGalleryChild } from 'src/core/models/api/ApiGallery';

interface GalleryTileProps {
	galleryholder: ApiGallery;
	galleries: ApiGalleryChild[];
	spaceAround?: StyleProp<ViewStyle>;
	onGalleryTilePress?: (data: ApiGallery) => void;
}

function GalleryTile({ galleryholder, galleries, spaceAround, onGalleryTilePress }: GalleryTileProps) {
	const navigationStore = useNavigationStore();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo((): {
		gridImg: ImageStyle;
		singleGridImg: ImageStyle;
		galleryTile: ViewStyle;
		galleryImageHolder: ViewStyle;
	} => {
		const tileWidth = (contentWidth - 85) / 2;

		return {
			gridImg: scalesWithColumn
				? {
						width: (tileWidth - 5) / 2,
						height: (tileWidth - 5) / 2,
						borderRadius: 10,
					}
				: {
						width: 69,
						height: 69,
						borderRadius: 0,
					},
			singleGridImg: scalesWithColumn
				? {
						width: tileWidth,
						height: tileWidth,
						borderRadius: 10,
					}
				: {
						width: 145,
						height: 145,
						borderRadius: 0,
						marginBottom: Dimens.margin5,
					},
			galleryTile: scalesWithColumn
				? {
						width: tileWidth,
						marginBottom: Dimens.margin20,
					}
				: {
						width: 145,
						height: 191,
						flexDirection: 'column',
					},
			galleryImageHolder: scalesWithColumn
				? {
						maxWidth: tileWidth,
					}
				: {
						maxWidth: 145,
						height: 145,
					},
		};
	}, [contentWidth]);

	const showEventGallery = (data: ApiGallery) => {
		navigationStore.navigate(Constants.screenRootGalleryHolder, { data });
		onGalleryTilePress?.(data);
	};

	const renderTileImage = () => {
		const filteredGalleries = galleries.filter((gallery) => String(gallery.hide_on_home) !== '1');
		const galleryHolder = galleryholder;
		const galleryImage =
			galleryHolder.data && 'page_image' in galleryHolder.data
				? (galleryHolder.data.page_image as string | undefined)
				: undefined;

		const gallerytiles = filteredGalleries.map((gallery, index) => {
			if (index < 4) {
				const childImage = gallery.data && 'page_image' in gallery.data ? gallery.data.page_image : undefined;
				return (
					<View key={gallery.name}>
						<FadeIn>
							<Image
								source={childImage ? { uri: childImage } : Images.placeholder}
								style={[
									sizedStyles.gridImg,
									{
										marginRight: checkIsEven(index) ? 5 : 0,
										marginBottom: checkIsEven(index) ? 5 : 0,
									},
								]}
							/>
						</FadeIn>
					</View>
				);
			}
			return null;
		});

		return (
			<View style={sizedStyles.galleryImageHolder}>
				<View style={styles.grid}>
					{galleryHolder.children.length > 3 ? (
						gallerytiles
					) : (
						<FadeIn>
							<Image
								defaultSource={Images.placeholder}
								source={galleryImage ? { uri: galleryImage } : Images.placeholder}
								style={sizedStyles.singleGridImg}
							/>
						</FadeIn>
					)}
				</View>

				<Text numberOfLines={Platform.OS === 'ios' ? 1 : undefined} style={styles.title}>
					{galleryHolder.event_name}
				</Text>
				<Text style={styles.subTitle}>{galleryHolder.total_photos}</Text>
			</View>
		);
	};

	if (galleryholder.total_photos > 0) {
		return (
			<TouchableOpacity style={[sizedStyles.galleryTile, spaceAround]} onPress={() => showEventGallery(galleryholder)}>
				{renderTileImage()}
			</TouchableOpacity>
		);
	}
	return null;
}

export default GalleryTile;

// Web included: the column is between 320 and 500 points wide depending on the window, so the
// tiles have to follow it. Android keeps its fixed sizes, which is what its design asks for.
const scalesWithColumn = Platform.OS !== 'android';

const styles = StyleSheet.create({
	grid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	title: {
		backgroundColor: Colors.transparent,
		textAlign: 'left',
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize17,
		marginTop: Dimens.margin5,
		marginBottom: scalesWithColumn ? undefined : Dimens.margin5,
	},
	subTitle: {
		textAlign: 'left',
		color: Colors.lightGrey,
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
		marginBottom: scalesWithColumn ? undefined : Dimens.margin5,
	},
});
